/**
 * Permissions Selector
 *
 * Derives call permissions from store state instead of storing them.
 * This eliminates the need for manual previous-value tracking and
 * subscription-based permission updates in CallManager.
 */
import { getCallPermissions } from 'Modules/softphone/resolver';
import type { CallPermissions } from 'Modules/softphone/types';
import type { SoftphoneStore } from '..';

/**
 * Derives the current call permissions from store state.
 */
export const selectPermissions = (state: SoftphoneStore): CallPermissions => {
  const currentCall = state.currentCallId
    ? state.activeCalls.get(state.currentCallId)
    : null;

  const callState = currentCall?.state ?? 'idle';
  const callCount = state.activeCalls.size;
  const hasConference = state.activeConference !== null;
  const currentCallInConference = currentCall?.conferenceId !== undefined && currentCall?.conferenceId !== null;

  const hasNonConferenceCall = Array.from(state.activeCalls.values()).some(
    (c) => !c.conferenceId
  );

  const base = getCallPermissions(callState);

  // Allow merge when:
  // 1. No conference: at least 2 calls and current call is active/held
  // 2. Active conference: allow merge if there's a non-conference call that can be added
  const canMerge = hasConference
    ? hasNonConferenceCall &&
      (callState === 'active' || callState === 'held')
    : callCount >= 2 &&
      (callState === 'active' || callState === 'held');

  // Transfer is allowed when the call is active or held, AND not part of a conference.
  // Disabled for ringing, connecting, ended, and all other states.
  // Also disabled when a transfer is already in progress (isTransferActive).
  const canTransfer =
    base.canTransfer &&
    (callState === 'active' || callState === 'held') &&
    !currentCallInConference &&
    !state.isTransferActive;

  // Disable video only when the current call is part of the conference.
  // When the user has a separate 1:1 call during a conference, video
  // should follow normal permissions for that call.
  const canUseVideo =
    hasConference && currentCallInConference
      ? false
      : base.canUseVideo;

  return { ...base, canMerge, canTransfer, canUseVideo };
};
